// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { loadPageByType } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getCurrentPage, getPageStateByType } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { nullPage } from "@insite/client-framework/Store/Data/Pages/PagesState";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getMyAccount } from "@stoneage/App/Store/Selectors/PageSelectors";
import { xScreen } from "@stoneage/XDL/Utilities";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showLeftRail = "showLeftRail",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showLeftRail]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const pageType = getCurrentPage(state).type;
    const navigationListLinks =
        (getWidgetsByPageId(state, getMyAccount(state).id).find((widget: WidgetProps) => {
            return widget.type === "Basic/NavigationList";
        })?.fields as any[]) ?? [];
    const myAccountPageState = getPageStateByType(state, "MyAccountPage");

    return {
        isMyAccountDashboard: pageType === "MyAccountPage", // MyAccountPage
        navigationListLinks,
        myAccountPageState: myAccountPageState.value || nullPage,
        myAccountPageIsLoading: myAccountPageState.isLoading,
    };
};
const mapDispatchToProps = {
    loadPageByType,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface MyAccountPageLayoutStyles {
    container?: InjectableCss;
    contentContainer?: InjectableCss;
    headerContainer?: InjectableCss;
    headerContainerSecondChild?: InjectableCss;
    headerContainerFirstChild?: InjectableCss;
    myAccountNavigationList?: InjectableCss;
    myAccountHeader?: TypographyProps;
}

const myAccountPageLayout: MyAccountPageLayoutStyles = {
    container: {
        css: css`
            display: flex;
            & > div {
                height: 100%;
            }
            ${xScreen("lte", "md")} {
                flex-wrap: wrap;
            }
        `,
    },
    contentContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            width: calc(100% - 270px);

            &:first-child {
                margin-right: 24px;
            }
            ${xScreen("lte", "md")} {
                width: 100%;
            }

            ${({ showLeftRail }: any) => {
                if (!showLeftRail) {
                    return `
                    width: 100%;
                    `;
                }
            }}
        `,
    },
    headerContainer: {
        css: css`
            display: flex;
            justify-content: flex-start;
            align-items: flex-end;
            margin-bottom: 24px;
        `,
    },
    headerContainerFirstChild: {
        css: css`
            margin-right: 32px;
            h1 {
                margin: 0;
                font-size: 28px;
            }

            ${xScreen("lte", "xs")} {
                margin-right: 20px;
            }
        `,
    },
    headerContainerSecondChild: {
        css: css`
            flex: 1;
        `,
    },
    myAccountNavigationList: {
        css: css`
            width: 270px;
            margin-right: 60px;
            ${xScreen("lte", "md")} {
                width: 100%;
                margin-right: 0;
            }
        `,
    },
    myAccountHeader: {
        variant: "h3",
        as: "h3",
        css: css`
            height: 24px;
            color: ${getColor("common.ui1")};
            font-size: 20px;
            font-weight: bold;
            line-height: 24px;
            margin-bottom: 28px;
        `,
    },
};

const styles = myAccountPageLayout;
const MyAccountPageLayout: FC<Props> = ({
    id,
    fields,
    isMyAccountDashboard,
    navigationListLinks,
    loadPageByType,
    myAccountPageState,
    myAccountPageIsLoading,
}) => {
    React.useEffect(() => {
        setTimeout(() => {
            // make sure to call the load function only once in the app life.
            if (!myAccountPageIsLoading && myAccountPageState.id === "" && !navigationListLinks?.length) {
                loadPageByType("MyAccountPage");
            }
        }, 500);
    }, []);

    return (
        <StyledWrapper {...styles.container}>
            {fields.showLeftRail && (
                <StyledWrapper {...styles.myAccountNavigationList}>
                    <Typography {...styles.myAccountHeader}>{translate("My Account")}</Typography>
                    {isMyAccountDashboard ? (
                        <Zone contentId={id} zoneName="Content03" />
                    ) : (
                        <StyledWrapper>
                            {createWidgetElement("Basic/NavigationList", {
                                fields: navigationListLinks,
                            })}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.contentContainer} {...({ showLeftRail: fields.showLeftRail } as any)}>
                <StyledWrapper {...styles.headerContainer}>
                    <StyledWrapper {...styles.headerContainerFirstChild}>
                        <Zone contentId={id} zoneName="Content00" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.headerContainerSecondChild}>
                        <Zone contentId={id} zoneName="Content01" />
                    </StyledWrapper>
                </StyledWrapper>
                <Zone contentId={id} zoneName="Content02" />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyAccountPageLayout),
    definition: {
        group: "XNGAGE" as any,
        fieldDefinitions: [
            {
                name: fields.showLeftRail,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
